import React, { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  useFocusTrap,
  useKeyboardNavigation,
  useScreenReader,
} from '../../hooks/useAccessibility';
import { useResponsive } from '../../hooks/useResponsive';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  description?: string;
  adminOnly?: boolean;
}

const getNavigation = (t: (key: string) => string, userRole?: string): NavItem[] => {
  const baseNavigation: NavItem[] = [
  {
    name: t('navigation.dashboard'),
    href: '/dashboard',
    description: 'View dashboard overview and statistics',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"
        />
      </svg>
    ),
  },
  {
    name: t('navigation.holidays'),
    href: '/holidays',
    description: 'Manage holidays and holiday schedules',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    name: t('navigation.pendingHolidays') || 'Pending Holidays',
    href: '/holidays/pending',
    description: 'Review and approve pending holiday requests',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    adminOnly: true,
  },
  {
    name: t('navigation.users'),
    href: '/users',
    description: 'Manage user accounts and permissions',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
        />
      </svg>
    ),
  },
  {
    name: t('navigation.reports'),
    href: '/reports',
    description: 'Generate and view reports',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    name: t('navigation.settings'),
    href: '/settings',
    description: 'Configure application settings and preferences',
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
];

  // Filter navigation based on user role
  if (userRole === 'user') {
    // Hide users tab and pending holidays for regular users
    return baseNavigation.filter(item => item.href !== '/users' && !(item as any).adminOnly);
  }
  
  // Admin users see all navigation items
  return baseNavigation;
};

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { announce } = useScreenReader();
  const { t, isRTL } = useI18n();
  const { user } = useAuth();
  const focusTrapRef = useFocusTrap(isOpen && isMobile);
  const navRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  
  const navigation = getNavigation(t, user?.role);

  // Keyboard navigation for nav items
  const {
    handleKeyDown: handleNavKeyDown,
    setActiveIndex,
    resetNavigation,
  } = useKeyboardNavigation(navRefs.current.filter(Boolean) as HTMLElement[], {
    orientation: 'vertical',
    loop: true,
    onSelect: (index) => {
      navRefs.current[index]?.click();
    },
  });

  // Announce sidebar state changes with more context
  useEffect(() => {
    if (isMobile) {
      if (isOpen) {
        announce(
          'Navigation menu opened. Use arrow keys to navigate, Enter to select, Escape to close.'
        );
        // Focus the first navigation item when opened
        setTimeout(() => {
          if (navRefs.current[0]) {
            navRefs.current[0].focus();
            setActiveIndex(0);
          }
        }, 100);
      } else {
        announce('Navigation menu closed');
        resetNavigation();
      }
    }
  }, [isOpen, isMobile, announce, setActiveIndex, resetNavigation]);

  // Enhanced keyboard handling for the entire sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Escape key to close sidebar
      if (event.key === 'Escape' && isOpen && isMobile) {
        event.preventDefault();
        onClose();
        return;
      }

      // Handle Tab key for focus management within sidebar
      if (event.key === 'Tab' && isOpen && isMobile) {
        const focusableElements = focusTrapRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }
      }
    };

    if (isOpen && isMobile) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, isMobile, onClose, focusTrapRef]);

  const handleNavClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  // Enhanced active state detection - completely custom logic
  const isActiveRoute = (href: string) => {
    const currentPath = location.pathname;
    const targetPath = href;

    // Handle root path
    if (targetPath === '/dashboard' && currentPath === '/') {
      return true;
    }

    // CRITICAL FIX: If we're on /holidays/pending, ONLY /holidays/pending should be active
    // This prevents /holidays from also being highlighted
    if (currentPath === '/holidays/pending') {
      // Only return true if this is the pending holidays route
      return targetPath === '/holidays/pending';
    }

    // If we're checking /holidays route, make sure we're NOT on /holidays/pending
    if (targetPath === '/holidays') {
      // If we're on pending, this should never be active (already handled above, but double-check)
      if (currentPath === '/holidays/pending') {
        return false;
      }
      // Exact match
      if (currentPath === '/holidays') {
        return true;
      }
      // Match other nested routes under /holidays (but not /holidays/pending)
      if (currentPath.startsWith('/holidays/') && currentPath !== '/holidays/pending') {
        return true;
      }
      return false;
    }

    // Exact match for all other routes
    if (currentPath === targetPath) {
      return true;
    }

    // For nested routes (like /reports/annual-leave-records matching /reports)
    // But exclude /dashboard and /holidays (already handled above)
    if (
      targetPath !== '/dashboard' &&
      targetPath !== '/holidays' &&
      currentPath.startsWith(targetPath + '/')
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      {/* Mobile sidebar overlay backdrop */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
          aria-hidden="true"
          aria-label={t('common.close') + ' ' + t('common.menu')}
          role="button"
          tabIndex={-1}
        />
      )}

      {/* Sidebar */}
      <aside
        id="navigation-sidebar"
        ref={isMobile ? focusTrapRef : undefined}
        className={`
          layout-sidebar fixed inset-y-0 z-50 flex flex-col transform transition-all duration-300 ease-in-out
          w-64 sm:w-72 md:w-64 lg:w-64 xl:w-72
          lg:translate-x-0 lg:static lg:inset-0 lg:z-auto lg:flex-shrink-0
          ${
            isMobile
              ? `shadow-2xl ${isOpen ? 'translate-x-0' : (isRTL ? 'translate-x-full' : '-translate-x-full')}`
              : 'translate-x-0 shadow-lg'
          }
          ${isRTL ? 'border-l' : 'border-r'}
        `}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
          [isRTL ? 'right' : 'left']: 0,
          [isRTL ? 'left' : 'right']: 'auto'
        }}
        aria-label={`${t('common.menu')}${isMobile ? (isOpen ? ` (${t('common.expand')})` : ` (${t('common.collapse')})`) : ''}`}
        role="complementary"
        aria-hidden={isMobile ? !isOpen : false}
        aria-modal={isMobile && isOpen ? 'true' : undefined}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="sidebar-header flex items-center justify-between h-16 px-4 sm:px-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className={`flex items-center min-w-0 flex-1 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <div className="sidebar-logo flex-shrink-0">
              <svg
                className="w-6 h-6 sm:w-8 sm:h-8"
                style={{ color: 'var(--color-primary)' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className={`${isRTL ? 'mr-2 sm:mr-3 flex-1' : 'ml-2 sm:ml-3'} text-lg sm:text-xl font-semibold truncate`} style={{ color: 'var(--color-text)', textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
              {t('common.holidayDashboard')}
            </h1>
          </div>

          {/* Close button for mobile */}
          {isMobile && (
            <button
              ref={closeButtonRef}
              type="button"
              className="sidebar-close-btn p-2 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-opacity-75"
              style={{ 
                color: 'var(--color-text-secondary)',
                backgroundColor: 'transparent'
              }}
              onClick={onClose}
              aria-label={t('common.close') + ' ' + t('common.menu')}
              aria-keyshortcuts="Escape"
              tabIndex={isOpen ? 0 : -1}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-border)';
                e.currentTarget.style.color = 'var(--color-text)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav
          className="mt-4 sm:mt-6 px-4 sm:px-6 flex-1 overflow-y-auto"
          onKeyDown={(e) => {
            // Only handle navigation keys within the nav area
            const navKeys = [
              'ArrowUp',
              'ArrowDown',
              'Home',
              'End',
              'Enter',
              ' ',
            ];
            if (navKeys.includes(e.key)) {
              handleNavKeyDown(e.nativeEvent);
            }
          }}
          role="navigation"
          aria-label="Primary navigation"
        >
          <ul
            className="space-y-1"
            role="menu"
            aria-label="Navigation menu"
            aria-orientation="vertical"
          >
            {navigation.map((item, index) => {
              // Calculate active state directly from location, not from NavLink
              const isActive = isActiveRoute(item.href);
              
              return (
                <li key={item.name} role="none">
                  <NavLink
                    ref={(el) => {
                      navRefs.current[index] = el;
                    }}
                    to={item.href}
                    className={`nav-item group flex items-center px-2 sm:px-3 py-2 sm:py-3 text-sm font-medium rounded-md transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-opacity-75 ${isRTL ? 'flex-row-reverse' : 'flex-row'} ${
                      isActive
                        ? `shadow-sm ${isRTL ? 'border-l-4' : 'border-r-4'}`
                        : 'hover:shadow-sm'
                    }`}
                    style={isActive ? {
                      backgroundColor: 'var(--color-primary-light)',
                      color: 'var(--color-primary)',
                      borderColor: 'var(--color-primary)'
                    } : {
                      color: 'var(--color-text)'
                    }}
                    onClick={handleNavClick}
                    aria-current={isActive ? 'page' : undefined}
                    aria-describedby={
                      item.description ? `nav-desc-${index}` : undefined
                    }
                    role="menuitem"
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'var(--color-border)';
                        e.currentTarget.style.color = 'var(--color-text)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--color-text)';
                      }
                    }}
                  >
                    <span
                      className={`nav-item-icon ${isRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 transition-colors duration-200`}
                      style={{
                        color: isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)'
                      }}
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    <span className={`nav-item-text truncate font-medium ${isRTL ? 'flex-1 text-right' : ''}`} style={{ textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>{item.name}</span>
                    {item.description && (
                      <span id={`nav-desc-${index}`} className="sr-only">
                        {item.description}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Live region for screen reader announcements */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          id="sidebar-announcements"
        />

        {/* Skip link target */}
        <div id="navigation" className="sr-only">
          Navigation menu
        </div>
      </aside>
    </>
  );
};
